import {
  VetaCenterConnection_Read,
  VetaCenterContract as VetaCenterContract_Read,
  VetaCenterContractWorkspace as VetaCenterContractWorkspace_Read,
  VetaCenterProjectLink_Read,
  VetaCenterReadinessItem_Read,
  VetaCenterSourceOperator_Read,
  VetaCenterTestTicket_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { ProjectTypeahead } from '~hooks/useProjects/models';

class VetaCenterContractWorkspace {
  constructor(
    private _workspace_guid: string,
    private _workspace_name: string | null,
  ) {}

  public get workspaceGuid() {
    return this._workspace_guid;
  }

  public get workspaceName() {
    return this._workspace_name;
  }

  public static parse(proto: VetaCenterContractWorkspace_Read) {
    return new VetaCenterContractWorkspace(proto.workspace_guid, proto.workspace_name);
  }
}

class VetaCenterContract {
  constructor(
    private _contract_guid: string,
    private _contract_id: string | null,
    private _agency_name: string | null,
    private _workspaces: VetaCenterContractWorkspace[],
  ) {}

  public get contractGuid() {
    return this._contract_guid;
  }

  public get contractId() {
    return this._contract_id;
  }

  public get agencyName() {
    return this._agency_name;
  }

  public get workspaces() {
    return this._workspaces;
  }

  public static parse(proto: VetaCenterContract_Read) {
    return new VetaCenterContract(
      proto.contract_guid,
      proto.contract_id,
      proto.agency_name,
      proto.workspaces.map(VetaCenterContractWorkspace.parse),
    );
  }
}

export class VetaCenterConnection {
  constructor(
    private _id: string,
    private _environment: VetaCenterConnection_Read['environment'],
    private _service_user: string,
    private _external_own_fleet_hauler_company_guid: string | null,
    private _contracts: VetaCenterContract[],
    private _contracts_discovered_at: Dayjs | null,
  ) {}

  public get id() {
    return this._id;
  }

  public get environment() {
    return this._environment;
  }

  public get serviceUser() {
    return this._service_user;
  }

  public get externalOwnFleetHaulerCompanyGuid() {
    return this._external_own_fleet_hauler_company_guid;
  }

  public get contracts() {
    return this._contracts;
  }

  public get contractsDiscoveredAt() {
    return this._contracts_discovered_at;
  }

  public isDiscoveringSince(baselineInMs: number | null | undefined) {
    return (
      baselineInMs !== undefined &&
      (this._contracts_discovered_at?.valueOf() ?? null) === baselineInMs
    );
  }

  public static parse(proto: VetaCenterConnection_Read) {
    return new VetaCenterConnection(
      proto.id,
      proto.environment,
      proto.service_user,
      proto.external_own_fleet_hauler_company_guid,
      proto.contracts.map(VetaCenterContract.parse),
      proto.contracts_discovered_at ? dayjs(proto.contracts_discovered_at) : null,
    );
  }
}

export class VetaCenterProjectLink {
  constructor(
    private _id: string,
    private _project: ProjectTypeahead,
    private _external_contract_guid: string,
    private _external_workspace_guid: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get project() {
    return this._project;
  }

  public get externalContractGuid() {
    return this._external_contract_guid;
  }

  public get externalWorkspaceGuid() {
    return this._external_workspace_guid;
  }

  public static parse(proto: VetaCenterProjectLink_Read) {
    return new VetaCenterProjectLink(
      proto.id,
      ProjectTypeahead.parse(proto.project),
      proto.external_contract_guid,
      proto.external_workspace_guid,
    );
  }
}

export class VetaCenterReadinessItem {
  constructor(
    private _kind: VetaCenterReadinessItem_Read['kind'],
    private _severity: VetaCenterReadinessItem_Read['severity'],
    private _record_type: VetaCenterReadinessItem_Read['record_type'],
    private _record_id: string,
    private _record_name: string,
  ) {}

  public get kind() {
    return this._kind;
  }

  public get severity() {
    return this._severity;
  }

  public get recordType() {
    return this._record_type;
  }

  public get recordId() {
    return this._record_id;
  }

  public get recordName() {
    return this._record_name;
  }

  public static parse(proto: VetaCenterReadinessItem_Read) {
    return new VetaCenterReadinessItem(
      proto.kind,
      proto.severity,
      proto.record_type,
      proto.record_id,
      proto.record_name,
    );
  }
}

export class VetaCenterSourceOperator {
  constructor(
    private _guid: string,
    private _name: string | null,
  ) {}

  public get guid() {
    return this._guid;
  }

  public get name() {
    return this._name;
  }

  public static parse(proto: VetaCenterSourceOperator_Read) {
    return new VetaCenterSourceOperator(proto.guid, proto.name);
  }
}

export class VetaCenterTestTicket {
  constructor(
    private _site_id: string,
    private _site_name: string,
    private _status: VetaCenterTestTicket_Read['status'],
    private _reason: string | null,
    private _sent_at: Dayjs | null,
  ) {}

  public get siteId() {
    return this._site_id;
  }

  public get siteName() {
    return this._site_name;
  }

  public get status() {
    return this._status;
  }

  public get reason() {
    return this._reason;
  }

  public get sentAt() {
    return this._sent_at;
  }

  public static parse(proto: VetaCenterTestTicket_Read) {
    return new VetaCenterTestTicket(
      proto.site_id,
      proto.site_name,
      proto.status,
      proto.reason,
      proto.sent_at ? dayjs(proto.sent_at) : null,
    );
  }
}
